import { useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { CheckCircle2, Eye, FileText, Loader2, Lock, Save, UploadCloud } from "lucide-react";
import { TextAreaField, TextField, SelectField, FieldShell } from "@/components/forms";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";
import { useToast } from "@/app/hooks/use-toast";
import { formatDate } from "@/utils/formatters/date.formatters";
import { candidateChecklistApi } from "@/services/endpoints/candidateChecklist";
import type {
  CandidateChecklistItem,
  CandidateChecklistItemUpdate,
  ChecklistOption,
} from "@/services/api/types";
import {
  apiErrorMessage,
  checklistOptions,
  dropdownChoice,
  dropdownChoices,
  fileNameFromKey,
  itemState,
  parseChecklistValue,
  toDateInputValue,
  uploadedFileKey,
  type ChecklistItemState,
} from "./checklistValues";

/** The upload route's multer limit in ems-api (`documentUpload`, 10 MB). */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/**
 * Types the API stores for a checklist upload: those both the route's
 * `documentFileFilter` and the `employee_document` destination accept. GIF
 * passes the first and fails the second; PowerPoint fails the first.
 */
const UPLOAD_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp";

const STATE_META: Record<ChecklistItemState, { label: string; style: CSSProperties }> = {
  approved: {
    label: "Approved",
    style: { background: "rgba(var(--pos-rgb),0.1)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.25)" },
  },
  rejected: {
    label: "Needs changes",
    style: { background: "hsl(var(--destructive) / 0.12)", color: "hsl(var(--destructive))", border: "1px solid hsl(var(--destructive) / 0.25)" },
  },
  submitted: {
    label: "Awaiting review",
    style: { background: "rgba(var(--acc2-rgb),0.1)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.25)" },
  },
  todo: {
    label: "To do",
    style: { background: "var(--landing-surface)", color: "var(--landing-text-muted)", border: "1px solid var(--landing-glass-border)" },
  },
};

interface ChecklistItemCardProps {
  item: CandidateChecklistItem;
  index: number;
  candidateId: string;
  /** The activation accepts answers (pending or approved). */
  checklistOpen: boolean;
  /** The candidate may change this item (open and not yet approved). */
  editable: boolean;
  onUpdated: (update: CandidateChecklistItemUpdate) => void;
}

/** Draft of a non-file answer, in the shape its input edits. */
type Draft =
  | { kind: "text"; text: string }
  | { kind: "options"; options: ChecklistOption[] }
  | { kind: "flag"; done: boolean }
  | { kind: "choice"; choice: string; choices: string[] };

function initialDraft(item: CandidateChecklistItem): Draft {
  const type = item.templateItem?.itemType;
  if (type === "checkbox" || type === "multiple_choice") {
    const options = checklistOptions(item);
    if (options) return { kind: "options", options };
    if (type === "checkbox") {
      const value = parseChecklistValue(item.value);
      return { kind: "flag", done: typeof value === "string" && value.trim().toLowerCase() === "true" };
    }
    return { kind: "options", options: [] };
  }
  if (type === "dropdown") {
    // Choices come from the template's option list; the row's value is only the saved choice.
    return { kind: "choice", choice: dropdownChoice(item), choices: dropdownChoices(item) };
  }
  if (type === "date") return { kind: "text", text: toDateInputValue(item.value) };
  const value = parseChecklistValue(item.value);
  return { kind: "text", text: typeof value === "string" || typeof value === "number" ? String(value) : "" };
}

/** The PATCH body value for a draft, or an error message the candidate can act on. */
function draftToValue(type: string | undefined, draft: Draft): { value?: unknown; error?: string } {
  switch (draft.kind) {
    case "options":
      return draft.options.length > 0 ? { value: draft.options } : { error: "This item has no options to choose from." };
    case "flag":
      return { value: draft.done ? "true" : "false" };
    case "choice":
      return draft.choice ? { value: [draft.choice] } : { error: "Choose an option." };
    case "text": {
      const text = draft.text.trim();
      if (!text) return { error: type === "date" ? "Pick a date." : "Enter an answer." };
      if (type === "number") {
        const n = Number(text);
        return Number.isFinite(n) ? { value: n } : { error: "Enter a number." };
      }
      return { value: text };
    }
  }
}

/** Read-only rendering of the saved answer. */
function AnswerText({ item }: { item: CandidateChecklistItem }) {
  const type = item.templateItem?.itemType;
  let text = "";
  if (type === "checkbox" || type === "multiple_choice") {
    const options = checklistOptions(item);
    if (options) {
      text = options.filter((o) => o.val).map((o) => o.label).join(", ");
    } else {
      const value = parseChecklistValue(item.value);
      text = typeof value === "string" && value.trim().toLowerCase() === "true" ? "Done" : "";
    }
  } else if (type === "dropdown") {
    text = dropdownChoice(item);
  } else if (type === "date") {
    const day = toDateInputValue(item.value);
    text = day ? formatDate(day, day, "MMM d, yyyy") : "";
  } else {
    const value = parseChecklistValue(item.value);
    text = typeof value === "string" || typeof value === "number" ? String(value) : "";
  }
  return (
    <p
      className="rounded-xl px-3.5 py-3 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]"
      style={{ ...surfaceStyle, color: text ? "var(--landing-text)" : "var(--landing-text-dim)" }}
    >
      {text || "No answer yet"}
    </p>
  );
}

function SmallButton({
  onClick,
  disabled,
  primary,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        (primary ? "btn-electric " : "") +
        "inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      }
      style={primary ? undefined : surfaceStyle}
    >
      {children}
    </button>
  );
}

/**
 * One activation checklist item: its title, instructions and review state, and
 * the input for its answer while the candidate may still change it.
 */
export function ChecklistItemCard({
  item,
  index,
  candidateId,
  checklistOpen,
  editable,
  onUpdated,
}: ChecklistItemCardProps) {
  const { toast } = useToast();
  const template = item.templateItem;
  const type = template?.itemType;
  const inputId = `checklist-item-${item.id}`;
  const state = itemState(item);
  const meta = STATE_META[state];

  const [draft, setDraft] = useState<Draft>(() => initialDraft(item));
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const change = (next: Draft) => {
    setDraft(next);
    setDirty(true);
    setError(null);
  };

  const save = async () => {
    const { value, error: invalid } = draftToValue(type, draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      const updated = await candidateChecklistApi.saveValue(candidateId, item.id, value);
      onUpdated(updated);
      setDirty(false);
      toast({ title: "Saved", description: `"${template?.title ?? "Checklist item"}" was saved.` });
    } catch (e) {
      toast({
        title: "Couldn't save",
        description: apiErrorMessage(e, "Please try again in a moment."),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("The file is larger than 10 MB.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const updated = await candidateChecklistApi.uploadFile(candidateId, item.id, file);
      onUpdated(updated);
      toast({ title: "File uploaded", description: `${file.name} was added to "${template?.title ?? "the checklist"}".` });
    } catch (e) {
      toast({
        title: "Upload failed",
        description: apiErrorMessage(e, "Please try again in a moment."),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const viewFile = async () => {
    // Opened before the await so a phone browser treats it as a user-initiated
    // pop-up; it is pointed at the signed URL once that arrives.
    const win = window.open("", "_blank");
    setOpening(true);
    try {
      const { url } = await candidateChecklistApi.getFileUrl(candidateId, item.id);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      win?.close();
      toast({
        title: "Couldn't open the file",
        description: apiErrorMessage(e, "Please try again in a moment."),
        variant: "destructive",
      });
    } finally {
      setOpening(false);
    }
  };

  const fileKey = type === "file_upload" ? uploadedFileKey(item) : "";

  let body: ReactNode;
  if (type === "file_upload") {
    body = (
      <FieldShell id={inputId} error={error} hint={editable ? "PDF, Word, Excel, CSV, text, or a JPEG, PNG or WebP image, up to 10 MB." : undefined}>
        {fileKey ? (
          <div className="flex min-w-0 items-center gap-2 rounded-xl px-3.5 py-3 text-sm" style={surfaceStyle}>
            <FileText className="h-4 w-4 shrink-0 text-[var(--acc2)]" />
            <span className="min-w-0 truncate">{fileNameFromKey(fileKey)}</span>
          </div>
        ) : (
          !editable && (
            <p className="rounded-xl px-3.5 py-3 text-sm" style={{ ...surfaceStyle, color: "var(--landing-text-dim)" }}>
              No file uploaded
            </p>
          )
        )}
        <input
          ref={fileInput}
          id={inputId}
          type="file"
          accept={UPLOAD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void upload(file);
          }}
        />
        {(editable || (fileKey !== "" && checklistOpen)) && (
          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            {editable && (
              <SmallButton primary={!fileKey} onClick={() => fileInput.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {uploading ? "Uploading…" : fileKey ? "Replace file" : "Upload file"}
              </SmallButton>
            )}
            {fileKey !== "" && checklistOpen && (
              <SmallButton onClick={() => void viewFile()} disabled={opening}>
                {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                View file
              </SmallButton>
            )}
          </div>
        )}
      </FieldShell>
    );
  } else if (!editable) {
    body = <AnswerText item={item} />;
  } else if (draft.kind === "options") {
    body = (
      <FieldShell error={error}>
        {draft.options.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--landing-text-dim)" }}>No options available.</p>
        ) : (
          <div className="space-y-2">
            {draft.options.map((option, i) => (
              <label
                key={`${option.key}-${i}`}
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3.5 py-3 text-sm"
                style={surfaceStyle}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 accent-[var(--acc2)]"
                  checked={option.val}
                  onChange={(e) =>
                    change({
                      kind: "options",
                      options: draft.options.map((o, j) => (j === i ? { ...o, val: e.target.checked } : o)),
                    })
                  }
                />
                <span className="min-w-0 [overflow-wrap:anywhere]">{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </FieldShell>
    );
  } else if (draft.kind === "flag") {
    body = (
      <FieldShell error={error}>
        <label className="flex cursor-pointer items-center gap-3 rounded-xl px-3.5 py-3 text-sm" style={surfaceStyle}>
          <input
            id={inputId}
            type="checkbox"
            className="h-4 w-4 shrink-0 accent-[var(--acc2)]"
            checked={draft.done}
            onChange={(e) => change({ kind: "flag", done: e.target.checked })}
          />
          Mark as done
        </label>
      </FieldShell>
    );
  } else if (draft.kind === "choice") {
    body =
      draft.choices.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--landing-text-dim)" }}>No options available.</p>
      ) : (
        <SelectField
          id={inputId}
          value={draft.choice}
          onValueChange={(choice) => change({ kind: "choice", choice, choices: draft.choices })}
          options={draft.choices.map((c) => ({ value: c, label: c }))}
          placeholder="Select an option…"
          error={error}
        />
      );
  } else if (type === "free_text" || !type) {
    body = (
      <TextAreaField
        id={inputId}
        value={draft.text}
        onChange={(text) => change({ kind: "text", text })}
        placeholder="Enter your answer…"
        error={error}
      />
    );
  } else {
    body = (
      <TextField
        id={inputId}
        type={type === "number" ? "number" : type === "date" ? "date" : "text"}
        inputMode={type === "number" ? "decimal" : undefined}
        value={draft.text}
        onChange={(text) => change({ kind: "text", text })}
        error={error}
      />
    );
  }

  return (
    <article className="rounded-2xl p-4 sm:p-5" style={glassCardStyle}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold"
            style={{ background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.18)" }}
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <h3
              className="text-base font-semibold leading-snug tracking-tight [overflow-wrap:anywhere]"
              style={{ color: "var(--landing-text)" }}
            >
              {template?.title ?? "Checklist item"}
              {template?.isRequired && <span className="ml-1 text-[var(--a2m-sig-ink-hi)]">*</span>}
            </h3>
            {template?.description && (
              <p
                className="mt-1 text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]"
                style={{ color: "var(--landing-text-muted)" }}
              >
                {template.description}
              </p>
            )}
          </div>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1 self-start rounded-full px-2.5 py-1 text-[11px] font-semibold"
          style={meta.style}
        >
          {state === "approved" ? <CheckCircle2 className="h-3 w-3" /> : null}
          {meta.label}
        </span>
      </div>

      {item.remarks && (
        <div
          className="mt-3 rounded-xl px-3.5 py-2.5 text-sm [overflow-wrap:anywhere]"
          style={{ background: "rgba(var(--acc2-rgb),0.05)", border: "1px solid rgba(var(--acc2-rgb),0.18)", color: "var(--landing-text)" }}
        >
          <span className="font-semibold">Remarks: </span>
          {item.remarks}
        </div>
      )}

      <div className="mt-4">{body}</div>

      {editable && type !== "file_upload" && (
        <div className="mt-3 flex justify-end">
          <SmallButton primary onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save"}
          </SmallButton>
        </div>
      )}

      {!editable && checklistOpen && state === "approved" && (
        <p className="mt-3 flex items-center gap-1.5 text-xs" style={{ color: "var(--landing-text-muted)" }}>
          <Lock className="h-3.5 w-3.5" />
          Approved items can no longer be changed.
        </p>
      )}
    </article>
  );
}
